/**
 * useConversations Hook
 * Hook para gerenciar conversas com React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ConversationQuery,
  CreateConversationRequest,
  UpdateConversationRequest,
  TransferRequest,
  CloseConversationRequest,
} from '@troia-v3/types';
import { chatService } from '../services';

/**
 * Buscar conversas com filtros e paginação
 */
export const useConversations = (query?: ConversationQuery, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['conversations', query],
    queryFn: ({ signal }) => chatService.getConversations(query, signal),
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
};

/**
 * Buscar conversa por ID
 */
export const useConversation = (id?: string) => {
  return useQuery({
    queryKey: ['conversation', id],
    queryFn: ({ signal }) => chatService.getConversationById(id!, signal),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Criar nova conversa
 */
export const useCreateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConversationRequest) => chatService.createConversation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Atualizar conversa
 */
export const useUpdateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateConversationRequest }) =>
      chatService.updateConversation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Transferir conversa — atribuir a user/team ou desatribuir
 *
 * Endpoint padronizado: POST /conversations/:id/transfer
 * Body: TransferRequest { toUserId?, toTeamId?, reason?, notes? }
 * Cobre tanto atribuição quanto reatribuição/desatribuição (substitui o antigo useAssignConversation).
 */
export const useTransferConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TransferRequest }) =>
      chatService.transferConversation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Fechar conversa
 */
export const useCloseConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: CloseConversationRequest }) =>
      chatService.closeConversation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Reabrir conversa
 */
export const useReopenConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => chatService.reopenConversation(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Silenciar/Dessilenciar conversa.
 * Usa `conversation.muted` (boolean computado per-user no backend) pra decidir
 * qual endpoint chamar. Lista bruta de userIds nunca chega no frontend.
 */
export const useToggleConversationMute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, currentlyMuted }: { id: string; currentlyMuted: boolean }) =>
      currentlyMuted
        ? chatService.unmuteConversation(id)
        : chatService.muteConversation(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};
